import type { NavigateFunction } from 'react-router';

import { LOGIN_ROUTE } from '../constants/auth';

export const getInitialsFromName = (name: string, lastName?: string): string => {
  if (!name) return '?';
  if (lastName) {
    return `${name[0]}${lastName[0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const getInitialsFromEmail = (email: string): string => {
  if (!email) return '?';
  const localPart = email.split('@')[0];
  const parts = localPart.split('.');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return localPart.substring(0, 2).toUpperCase();
};

export const toKebabCase = (str: string) => str.replaceAll(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

export const trimmedOrUndefined = (value: string | null | undefined): string | undefined => value?.trim() || undefined;

const normalizeField = (v: unknown): unknown => (typeof v === 'string' ? v.trim() : v);

export const isDraftEqual = <T extends object>(a: T, b: T): boolean => {
  const keysA = Object.keys(a) as (keyof T)[];
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((key) => normalizeField(a[key]) === normalizeField(b[key]));
};

const isExternalUrl = (url: string): boolean => {
  return /^https?:\/\//i.test(url);
};

export const redirectToPage = (navigate: NavigateFunction, redirectTo = LOGIN_ROUTE, target = '') => {
  isExternalUrl(redirectTo) ? window.open(redirectTo, target) : void navigate(redirectTo);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
};

export const getExtension = (name: string) => {
  const index = name.lastIndexOf('.');
  return index === -1 ? '' : name.slice(index).toLowerCase();
};

export const isFileValid = (file: File, accept: string[], maxSizeMb: number): boolean => {
  const ext = getExtension(file.name);
  const allowedExts = accept.map((s) => s.trim().toLowerCase()).filter(Boolean);
  return allowedExts.includes(ext) && file.size <= maxSizeMb * 1_048_576;
};

/** Saves a base64 payload the API returned inline (no URL) as a local file. */
export const downloadBase64File = (base64: string, fileName: string, mimeType: string): void => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.codePointAt(i) ?? 0;

  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

/** Result of trying to surface a PDF to the user:
 *  - `opened`: shown in a new browser tab
 *  - `downloaded`: saved to disk as a fallback when the popup was blocked
 *  - `failed`: neither worked → the caller should surface an error */
export type OpenPdfOutcome = 'opened' | 'downloaded' | 'failed';

/** Best-effort file name for the download fallback, derived from the URL path. */
const derivePdfFileName = (url: string, fallback = 'quote.pdf'): string => {
  try {
    const { pathname } = new URL(url);
    const name = decodeURIComponent(pathname.slice(pathname.lastIndexOf('/') + 1));
    return name.toLowerCase().endsWith('.pdf') ? name : fallback;
  } catch {
    return fallback;
  }
};

/** Surfaces a ready PDF to the user, robust against popup blocking.
 *
 *  Because the URL only becomes available after async polling, the browser's
 *  user-activation window is usually gone by the time we get here and
 *  `window.open` gets silently blocked. So we cascade:
 *   1. Open in a new tab (never over the current one). We omit `noopener` so the
 *      returned reference lets us detect a blocked popup, then sever `opener` for
 *      security. If it opened → done.
 *   2. Fallback: fetch the file (cross-origin S3) and download the blob, which
 *      forces a real download without navigating away. Requires bucket CORS.
 *   3. If both fail → `failed`, so the caller can show an error toast.
 *
 *  The URL is signed by CloudFront: re-encoding any part of it breaks the signature. */
export const openOrDownloadPdf = async (url: string): Promise<OpenPdfOutcome> => {
  try {
    const win = window.open(url, '_blank');
    if (win) {
      win.opener = null;
      return 'opened';
    }
  } catch {
    /* popup blocked → fall through to the download fallback */
  }

  try {
    const res = await fetch(url);
    if (!res.ok) return 'failed';

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = derivePdfFileName(url);
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    return 'downloaded';
  } catch {
    return 'failed';
  }
};
